import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../third-party-integration/amplitude';
import {
  Dialog,
  IconDescription,
  BUTTON_VARIANTS,
} from 'cdk-radial';
import { TextButton } from '../../components/Buttons/TextButton';
import { Button } from '../../components/Buttons/Button';
import { IAdoptEaseWorkflow } from '../../types';
import launchExternal from '../../util/launchExternal';
import { AMPLITUDE_EVENTS } from '../../constants';
import { useAdoptEaseStore } from './useAdoptEaseStore';
import { useWebViewStore } from '../webview/useWebViewStore';
import { useUpdateUserAdoptEaseUsage } from './useAdoptEase';

export const StyledIconDescription = styled(IconDescription)`
  width: 30px;
  height: 30px;
  fill: #196FE1;
`;

export const StyledDiv = styled.div`
  display: flex;
  @media (max-width: 625px) {
    display: grid;
  }
`;

export const StyledDialog = styled(Dialog)`
  min-width: 560px;
  max-width: 700px;
  width: 60vw;
  margin-top: 60px;
  z-index: 9999;
  height: auto;
  max-height: 400px;
  font-size: 24px;
  font-weight: 800;
  @media (max-width: 958px) {
    width: 92vw;
  }
  @media (max-width: 470px) {
    min-width: 10vw;
    max-width: 100%;
    width: 92vw;
    top: 310px;
  }
  @media (max-width: 570px) {
    min-width: 10vw;
    max-width: 100%;
    width: 92vw;
  }
`;

interface AdoptEaseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: () => void;
  onCdkUniversityClick?: () => void;
}

interface FooterProps {
  onClose: () => void;
  onProceed: () => void;
}

interface AdoptEaseDefaultConfigItem {
  workflow?: string;
  workflowHelpLink?: string;
  appHelpLink?: string;
}

const AdoptEaseDialogFooter = ({ onClose, onProceed }: FooterProps): JSX.Element => {
  const { trackEvent } = useAnalytics();
  const history = useHistory();
  const { mutate: mutateAdoptEaseUpdate } = useUpdateUserAdoptEaseUsage();
  const workflows = useAdoptEaseStore((state: any) => state.filteredAdoptEaseWorkflows) as IAdoptEaseWorkflow[];
  const { t } = useTranslation();

  const updateAdoptEaseUsage = () => {
    const now = (new Date());
    const record: Record<string, Date> = workflows.reduce<Record<string, Date>>((accumulated, item) => {
      accumulated[item!.workflow.toString()] = now;
      return accumulated;
    }, {});
    mutateAdoptEaseUpdate(record);
  };

  return (
    <StyledDiv style={{
      padding: '50px 16px 16px 5px',
      justifyContent: 'space-between',
    }}>
      <TextButton
        style={{ color: '#196FE1', fontSize: '14px', fontWeight: '550', minWidth: '170px', maxWidth: '190px' }}
        text={t('adoptease.proceed_drive')}
        onClick={() => {
          trackEvent(AMPLITUDE_EVENTS.RECOMMENDATION_PROCEED_DRIVE, {
            Workflows: JSON.stringify(workflows || []),
          });
          onClose();
          onProceed();
        }}
      />
      <div>

        <Button
          style={{ backgroundColor: '#E8F1FC', color: '#155EBF', fontSize: '14px', fontWeight: '500' }}
          variant={BUTTON_VARIANTS.PRIMARY}
          text={t('adoptease.remind_me')}
          onClick={() => {
            trackEvent(AMPLITUDE_EVENTS.RECOMMENDATION_REMIND_ME);
            onClose();
            onProceed();
            updateAdoptEaseUsage();
          }}
        />

        <Button
          style={{ backgroundColor: '#196FE1', fontSize: '14px', fontWeight: '500', marginLeft: '10px' }}
          variant={BUTTON_VARIANTS.PRIMARY}
          text={t('adoptease.go_workflow')}
          onClick={() => {
            trackEvent(AMPLITUDE_EVENTS.OPEN_RECOMMENDATION_WORKFLOW);
            history.push('workflows');
            onClose();
          }}
        />
      </div>

    </StyledDiv>
  );
};

const AdoptEaseDriveDialog = ({
  isOpen,
  onClose,
  onProceed,
}: AdoptEaseDialogProps): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const { setCustomWebView } = useWebViewStore();

  const onCdkuOpen = (url: string) => {
    history.push(`/unify/view/AE/cta`);
    setCustomWebView({ id: 'AE', url });
  };

  const { trackEvent } = useAnalytics();

  const filteredAdoptEaseWorkflows = useAdoptEaseStore(
    (state: any) => state.filteredAdoptEaseWorkflows
  ) as IAdoptEaseWorkflow[];
  const adoptEaseDefaultConfigData = useAdoptEaseStore(
    (state: any) => state.adoptEaseDefaultConfigData
  ) as AdoptEaseDefaultConfigItem[];

  const isSingleWorkflow = filteredAdoptEaseWorkflows?.length === 1;
  const primaryWorkflow = filteredAdoptEaseWorkflows?.[0];
  const primaryWorkflowConfig = adoptEaseDefaultConfigData?.find(
    item => item.workflow === primaryWorkflow?.modernWorkflow
  );

  const handleHelpClick = () => {
    trackEvent(AMPLITUDE_EVENTS.OPEN_RECOMMENDATION_HELP);
    const helpLink = isSingleWorkflow
      ? primaryWorkflowConfig?.workflowHelpLink
      : primaryWorkflowConfig?.appHelpLink;

    if (!helpLink) {
      onClose();
      return;
    }

    launchExternal(helpLink) || onCdkuOpen(helpLink);
    onClose();
  };

  const handleDismiss = () => {
    trackEvent(AMPLITUDE_EVENTS.RECOMMENDATION_DISMISS, {
      Workflows: JSON.stringify(filteredAdoptEaseWorkflows || []),
    });
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      trackEvent(AMPLITUDE_EVENTS.RECOMMENDATION_MODAL_SEEN, {
        Workflows: JSON.stringify(filteredAdoptEaseWorkflows || []),
      });
    }
  }, [isOpen, trackEvent, filteredAdoptEaseWorkflows]);

  return (
    <div>
      <StyledDialog
        isOpen={isOpen}
        id="recomm-dialog"
        data-testid="adoptease-recomm-dialog"
        onClose={handleDismiss}
        title={t('adoptease.recommended')}
        renderCustomFooterComponent={() => (
          <AdoptEaseDialogFooter onClose={onClose} onProceed={onProceed} />
        )}
        hideCloseButton={true}
        disableOverlayClicked={true}
      >
        {isSingleWorkflow ? (
          <div style={{ fontSize: '14px', fontWeight: '350' }}>
            {`${t('adoptease.notice_workflow')} ${primaryWorkflow?.function}. ${t('adoptease.encourage_workflow')} ${primaryWorkflow?.modernWorkflow}.`}
          </div>
        ) : (
          <div style={{ fontSize: '14px', fontWeight: '350' }}>
            {t('adoptease.notice_workflows')}
          </div>
        )}
        <div>

          {isSingleWorkflow ? (
            <div>
              <br />
              <div style={{ fontSize: '14px', fontWeight: '350' }}>{t('adoptease.review')} </div>
              <br />
              <StyledIconDescription />
              <TextButton
                style={{ color: '#196FE1', fontSize: '14px', fontWeight: '600', bottom: '10px' }}
                text={`${primaryWorkflow?.modernWorkflow} ${t('adoptease.help')}`}
                onClick={handleHelpClick}
              />
            </div>
          ) : (
            <div>
              <br />
              <div style={{ fontSize: '14px', fontWeight: '350' }}>{t('adoptease.review')} </div>
              <br />
              <StyledIconDescription />
              <TextButton
                style={{ color: '#196FE1', fontSize: '14px', fontWeight: '600', bottom: '10px' }}
                text={`Dealership Accounting ${t('adoptease.help')}`}
                onClick={handleHelpClick}
              />
            </div>
          )}
        </div>
      </StyledDialog>
    </div>
  );
};

export default AdoptEaseDriveDialog;
